import Box from "@mui/material/Box";
import formatMoney from "lib/formatMoney";
import validateEmail from "lib/validateEmail";

const maxRentalLTV = 0.8;
// max Bridge loan to after repair value
const maxBridgeLTARV = 0.75;
const minRentalLoan = 50000;
const minRentalRefinanceLoan = 80000;
const minBridgeLoan = 50000;
const slowFlipMin = 10000;

const unitSizeLimiter = (loanType, propertyType, numberOfUnits, answer) => {
  switch (loanType) {
    case "DSCR":
      if (propertyType === "Single family" && parseInt(answer.a_string) < 600) {
        return answer.q_id + "1";
      }
      if (propertyType === "Condominium" && parseInt(answer.a_string) < 500) {
        return answer.q_id + "1";
      }

      if (
        numberOfUnits >= 2 &&
        numberOfUnits <= 4 &&
        parseInt(answer.a_string) < 400
      ) {
        return answer.q_id + "1";
      }

      if (numberOfUnits > 5 && parseInt(answer.a_string) < 400) {
        return answer.q_id + "1";
      }
      break;
    case "Fix and flip":
      if (propertyType === "Single family" && parseInt(answer.a_string) < 700) {
        return answer.q_id + "1";
      }
      if (propertyType === "Condominium" && parseInt(answer.a_string) < 500) {
        return answer.q_id + "1";
      }
      if (
        numberOfUnits >= 2 &&
        numberOfUnits <= 4 &&
        parseInt(answer.a_string) < 500
      ) {
        return answer.q_id + "1";
      }
      break;
    case "Ground up construction":
      break;
  }
};
// this is the questions tree

// its packed as an array for ease of access by the end components
// to avoid tree search algoes and simplify access

// the tree layout is captured and adjusted through consensus between stakeholders
// on a visual board: https://miro.com/app/board/uXjVOSLJYlk=/
// no adjustments to this array or structure should take place without adjusting
// and building consensus on the MIRO board first

// keys: all lower case, using snake_case
const questions = [
  {
    q_id: "0",
    q_title: "Loan type", // loanSubtype
    q_description: (answer, answers, additionalData) => {
      // return "Please select a loan type";
      return "";
    },
    node_type: "node",
    a_type: "mc",
    a_opts: (answers) => {
      return [
        "DSCR",
        "Fix and flip", //UPDATE, used to be "Fix and flip", now "Fix and flip"
        "Bridge", // same as fix and flip
        "Hard money", // same as fix and flip
        "Slow flip",
        "Low balance", // same as Slow flip
        "Ground up construction",
      ];
    },
    q_next_step_func: (answer, answers, questions) => {
      // We don't do short term, so ok to boot them here
      // UPDATE 06/17/2023 we now do short term, mvp treat like DSCR
      // if (answer.a_string === "Short-term rental") {
      //   return answer.q_id + "4";
      // }

      return answer.q_id + "0";
    },
    q_child_nodes: [0, 1],
    q_validation_func: (answerVal) => {
      // this MC so any option clicked is valid
      return true; //isValid
    },
    q_invalid_answer_help_text: "",
    a_error: false,
  },
  // {
  //   q_id: "04",
  //   q_title: "Out of scope",
  //   q_description: (answer, answers, additionalData) => {
  //     return "Sorry, our guidelines won't let us underwrite short term rental loans at this time due to market conditions.";
  //   },
  //   a_type: "leaf",
  //   node_type: "leaf",
  //   q_child_nodes: [],
  // },
  {
    q_id: "00",
    q_title: "Investment experience",
    q_description: (answer, answers, additionalData) => {
      return "Number of investment properties owned in the last 10 years. Rented, flipped or rehabbed. 0 is a valid input.";
    },
    a_type: "number",
    a_input_type: "number",
    a_opts: (answers) => {
      // for number, this is min and man, a_opts[0] is min, a_opts[1] is max
      return [0];
    },
    q_next_step_func: (answer, answers, questions) => {
      // if 0 investment properties,
      // send to LEAF
      // UPDATE: we now allow 0 experienced people
      // since we quote LTV based on experience
      // they will just have a lower LTV
      // if (answer.a_string === "0") {
      //   return answer.q_id + "1";
      // }
      // UPDATE: ground up needs min 2 exp

      const loanType = answers?.filter((answer) => {
        return answer.q_title === "Loan type";
      })[0]?.a_string;

      if (answer.a_string < 2 && loanType === "Ground up construction") {
        return answer.q_id + "1";
      }

      return answer.q_id + "0";
    },
    q_child_nodes: [0],
    node_type: "node",
    q_validation_func: (answer) => {
      if (answer.a_string < 0) {
        return false; //isValid
      }
      return true; //isValid
    },
    q_invalid_answer_help_text: "Please enter a number above 0",
    a_error: false,
  },
  {
    q_id: "001",
    q_title: "Out of scope",
    q_description: (answer, answers, additionalData) => {
      return (
        <Box>
          We need to verify a minimum of 2 ground up experience among members of
          your borrowing entity.
          <br />
          <br />
          If you do not have this, please consider partnering with an
          experienced ground up construction developer as a member of your
          borrowing entity. Otherwise, we will not be able to fund this
          transaction.
          <br />
          <br />
        </Box>
      );
    },
    a_type: "leaf",
    node_type: "leaf",
    q_child_nodes: [],
  },
  {
    q_id: "000",
    q_title: "Credit score",
    q_description: (answer, answers, additionalData) => {
      // return "Self reported credit score. Does not affect loan terms but does affect eligibility.";
      return "";
    },
    a_type: "number",
    a_opts: (answers) => {
      return [550, 850];
    },
    q_next_step_func: (answer, answers, questions) => {
      // if score below 620,
      // UPDATE: min score used to be 620,
      // but we got exceptions on good deals for scores as low as 575
      // LEAF

      const loanType = answers?.filter((answer) => {
        return answer.q_title === "Loan type";
      })[0]?.a_string;

      if (loanType === "DSCR" && answer.a_string < 660) {
        return answer.q_id + "1";
      }

      if (loanType === "Fix and flip" && answer.a_string < 680) {
        return answer.q_id + "1";
      }

      if (answer.a_string < 620) {
        return answer.q_id + "1";
      }

      return answer.q_id + "0";
    },
    q_child_nodes: [0, 1],
    node_type: "node",
    q_validation_func: (answerVal) => {
      if (answerVal < 550 || answerVal > 850) {
        return false; //isValid
      }
      return true; //isValid
    },
    q_invalid_answer_help_text: "Please enter a number between 550 and 850",
    a_error: false,
  },
  {
    q_id: "0001",
    q_title: "Outside of scope",
    q_description: (answer, answers, additionalData) => {
      const loanType = answers?.filter((answer) => {
        return answer.q_title === "Loan type";
      })?.[0]?.a_string;

      let answerString = "";
      if (loanType === "DSCR") {
        answerString =
          "For DSCR, our minumum credit score is 660. Credit score of 720+, gets best rates";
      } else if (loanType === "Fix and flip") {
        answerString =
          "For Fix and flip (Bridge or hard money loans), our minumum credit score is 680. Credit score of 720+, gets best rates";
      } else {
        answerString = "Our minimum score is 620 across all loan products";
      }

      return (
        <Box>
          Sorry, we can't provide this loan.
          <br />
          <br />
          Credit score is too low. {answerString}
          <br />
          <br />
        </Box>
      );
    },
    a_type: "leaf",
    node_type: "leaf",
    q_child_nodes: [],
  },
  {
    q_id: "0000",
    q_title: "Borrowing entity",
    q_description: (answer, answers, additionalData) => {
      // return "LLC as borrowing entity is strongly preferred. Underwriter approval is required for loans to be funded in personal name and can delay processing.";
      return "";
    },
    node_type: "node",
    a_type: "mc",
    a_opts: (answers) => {
      return ["LLC", "Corporation", "Trust", "Personal name"];
    },
    q_next_step_func: (answer, answers, questions) => {
      // if Personal name
      // or not doesnt matter for rentals, just a data point for us
      const loanType = answers?.filter((answer) => {
        return answer.q_title === "Loan type";
      })[0]?.a_string;

      // Fix & flip must be in name of an LLC
      if (loanType === "Fix and flip" && answer.a_string === "Personal name") {
        // cant do personal name loans
        // for bridge
        return answer.q_id + "1";
      }
      if (
        loanType === "Fix and flip" ||
        loanType === "Ground up construction"
      ) {
        return answer.q_id + "00"; // skip portfolio question
      }

      return answer.q_id + "0";
    },
    q_child_nodes: [0, 1],
    q_validation_func: (answer) => {
      // this MC so any option clicked is valid
      return true; //isValid
    },
    q_invalid_answer_help_text: "",
    a_error: false,
  },
  {
    q_id: "00000",
    q_title: "Portfolio loan",
    q_description: (answer, answers, additionalData) => {
      return "Is this a portfolio loan? (More than one property in the loan)";
    },
    node_type: "node",
    a_type: "mc",
    a_opts: (answers) => {
      return ["No", "Yes"];
    },
    q_next_step_func: (answer, answers, questions) => {
      // NEW
      if (answer.a_string === "Yes") {
        // Always ask how many units next, before splitting type flow
        // because make human logically to do it now
        // and flow split move to net q // which kind of returns to how numbering was previously
        return answer.q_id + "1";
      }

      return answer.q_id + "0";
    },
    q_child_nodes: [0, 1],
    q_validation_func: (answerVal) => {
      return true; //isValid
    },
    q_invalid_answer_help_text: "",
    a_error: false,
  },
  {
    q_id: "000001",
    q_title: "Number of properties",
    q_description: (answer, answers, additionalData) => {
      return "How many properties are part of this portfolio loan?";
    },
    node_type: "node",
    a_type: "number",
    a_input_type: "number",
    a_opts: (answers) => {
      return [];
    },
    q_next_step_func: (answer, answers, questions) => {
      return answer.q_id + "0";
    },
    q_child_nodes: [0],
    q_validation_func: (answerVal) => {
      if (answerVal < 1) {
        return false; //isValid
      }
      return true; //isValid
    },
    q_invalid_answer_help_text: "Please enter a value above 0",
    a_error: false,
  },
  {
    q_id: "0000010",
    q_title: "Number of units",
    q_description: (answer, answers, additionalData) => {
      const portfolioAnswer = answers?.find(
        (answer) => answer.q_title === "Portfolio loan"
      )?.a_string;

      if (portfolioAnswer === "Yes") {
        return "How many total units are part of the portfolio?";
      }
      return "How many units are part of this property?";
    },
    node_type: "node",
    a_type: "number",
    a_input_type: "number",
    a_opts: (answers) => {
      return [];
    },
    q_next_step_func: (answer, answers, questions) => {
      return answer.q_id + "0";
    },
    q_child_nodes: [0],
    q_validation_func: (answerVal) => {
      if (answerVal < 1) {
        return false; //isValid
      }
      return true; //isValid
    },
    q_invalid_answer_help_text: "Please enter a value above 0",
    a_error: false,
  },
  {
    q_id: "00000100",
    q_title: "Smallest unit size",
    q_description: (answer, answers, additionalData) => {
      const portfolio = answers?.filter((answer) => {
        return answer.q_title === "Portfolio";
      })[0]?.a_string;

      const propertyType = answers?.filter((answer) => {
        return answer.q_title === "Property type";
      })[0]?.a_string;

      let textString;
      if (portfolio === "Yes") {
        textString = "portfolio";
      } else {
        textString = propertyType;
      }

      return `What is the square foot size of the smallest units in the ${textString}?`;
    },
    node_type: "node",
    a_type: "number",
    a_input_type: "",
    a_opts: (answers) => {
      return [0];
    },
    q_next_step_func: (answer, answers, questions) => {
      const propertyType = answers.filter((answer) => {
        return answer.q_title === "Property type";
      })?.[0]?.a_string;

      let numberOfUnits = answers.filter((answer) => {
        return answer.q_title === "Number of units";
      })?.[0]?.a_string;

      if (!numberOfUnits) {
        numberOfUnits = {
          Duplex: 2,
          Triplex: 3,
          Quadplex: 4,
        }[propertyType];
      }

      const loanType = answers?.filter((answer) => {
        return answer.q_title === "Loan type";
      })[0]?.a_string;

      const unitSizeLimiterReturn = unitSizeLimiter(
        loanType,
        propertyType,
        numberOfUnits,
        answer
      );

      if (unitSizeLimiterReturn) {
        return unitSizeLimiterReturn;
      }

      // if all checks out, continue
      if (
        [
          "Duplex",
          "Triplex",
          "Quadplex",
          "5+ unit multi-family",
          "Mixed use",
        ].includes(propertyType)
      ) {
        return "00000000"; // address
      }

      // return answer.q_id + "0"; // next
      return "000000000"; // Loan purpose
    },
    q_child_nodes: [0],
    q_validation_func: (answerVal) => {
      if (answerVal < 1) {
        return false; //isValid
      }
      return true; //isValid
    },
    q_invalid_answer_help_text: "Please enter a dollar amount above 0",
    a_error: false,
  },
  {
    q_id: "0000011",
    q_title: "Out of scope",
    q_description: (answer, answers, additionalData) => {
      return (
        <Box>
          Sorry, we can't provide this loan.
          <br />
          <br />
          Our loan program for Fix and flip (Bridge, Hard money) loans doesn't
          allow for 5+ unit multifamily or mixed used properties.
          <br />
          <br />
        </Box>
      );
    },
    a_type: "leaf",
    node_type: "leaf",
    q_child_nodes: [],
  },
  {
    q_id: "000001001",
    q_title: "Out of scope",
    q_description: (answer, answers, additionalData) => {
      return (
        <Box>
          Sorry, we can't provide this loan.
          <br />
          <br />
          The average unit size is too small and falls outside of our lending
          guidelines.
          <br />
          <br />
        </Box>
      );
    },
    a_type: "leaf",
    node_type: "leaf",
    q_child_nodes: [],
  },

  {
    q_id: "00001",
    q_title: "Out of scope",
    q_description: (answer, answers, additionalData) => {
      return (
        <Box>
          Sorry, we can't provide this loan.
          <br />
          <br />
          Fix & rent loans must be in the name of an LLC <br />
          <br />
          <br />
        </Box>
      );
    },
    a_type: "leaf",
    node_type: "leaf",
    q_child_nodes: [],
  },
  {
    q_id: "000000",
    q_title: "Property type",
    q_description: (answer, answers, additionalData) => {
      return "What is the property type?";
    },
    node_type: "node",
    a_type: "mc",
    a_input_type: "",
    a_opts: (answers) => {
      return [
        "Single family",
        "Duplex",
        "Triplex",
        "Quadplex",
        "Condominium",
        "5+ unit multi-family",
        "Mixed use",
      ];
    },
    q_next_step_func: (answer, answers, questions) => {
      const loanType = answers?.filter((answer) => {
        return answer.q_title === "Loan type";
      })[0]?.a_string;

      if (
        loanType === "Fix and flip" &&
        ["5+ unit multi-family", "Mixed use"].includes(answer.a_string)
      ) {
        return "0000011"; // error out dont procide
      }

      if (["Duplex", "Triplex", "Quadplex"].includes(answer.a_string)) {
        return "00000100"; // average unit size
      }

      if (["5+ unit multi-family", "Mixed use"].includes(answer.a_string)) {
        return "0000010"; // number of units
      }

      return answer.q_id + "0"; // SF and condo
    },
    q_child_nodes: [0, 1],
    q_validation_func: (answerVal) => {
      // all MC choices are valid
      return true; //isValid
    },
    q_invalid_answer_help_text: "Please enter a dollar amount above 0",
    a_error: false,
  },
  {
    q_id: "0000000",
    q_title: "Unit size",
    q_description: (answer, answers, additionalData) => {
      return "What is the estimated or actual size of the unit in square feet (sqft)?";
    },
    node_type: "node",
    a_type: "number",
    a_input_type: "", // redo this as square feet
    a_opts: (answers) => {
      return [0];
    },
    q_next_step_func: (answer, answers, questions) => {
      // Single family (1 unit): Minimum 700 sq. ft.
      // Condominiums: Minimum 500 sq. ft
      const propertyType = answers.filter((answer) => {
        return answer.q_title === "Property type";
      })?.[0]?.a_string;

      const loanType = answers?.filter((answer) => {
        return answer.q_title === "Loan type";
      })[0]?.a_string;

      // Default to 1 for single family and condos
      let numberOfUnits = 1;

      const unitSizeLimiterReturn = unitSizeLimiter(
        loanType,
        propertyType,
        numberOfUnits,
        answer
      );

      if (unitSizeLimiterReturn) {
        return unitSizeLimiterReturn;
      }

      return answer.q_id + "0";
    },
    q_child_nodes: [0],
    q_validation_func: (answerVal) => {
      if (answerVal < 1) {
        return false; //isValid
      }
      return true; //isValid
    },
    q_invalid_answer_help_text: "Please enter a number above 0 sqft",
    a_error: false,
  },
  {
    q_id: "00000000",
    q_title: "Address",
    q_description: (answer, answers, additionalData) => {
      const portfolioLoan = answers.filter((answer) => {
        return answer.q_title === "Portfolio loan";
      })?.[0]?.a_string;

      if (portfolioLoan === "Yes") {
        // its a portfolio loan
        return "Enter the STATE where the portfolio is located.";
      }

      return "Enter property address. This information is never shared with anyone and is only used for the purpose of this loan quote. We need full address for accurate quote.";
    },
    node_type: "node",
    a_type: "address",
    a_opts: (answers) => {
      return [];
    },
    q_next_step_func: (answer, answers, questions) => {
      // For now commented out
      // let anyone proceed
      // TODO make it show up as loading.. gmaps that is
      // const addressArr = answer?.a_string?.split(",");
      // const country = addressArr?.[addressArr.length - 1].trim();

      // if (!answer.a_string) {
      //   // must have an answer full address or zip
      //   return answer.q_id + "1";
      // }
      // if (country && country !== "USA") {
      //   // all defaults have to return '0' when variable isnt specified
      //   // only in US
      //   return answer.q_id + "1";
      // }

      return answer.q_id + "0";
    },
    q_child_nodes: [0],
    q_validation_func: (answerVal) => {
      //   make sure not false and a string of none 0 length
      if (!answerVal && !answerVal.length > 0) {
        return false;
      }
      return true; //isValid
    },
    q_invalid_answer_help_text: "Enter full address or zip",
    a_error: false,
  },
  {
    q_id: "00000001",
    q_title: "Out of scope",
    q_description: (answer, answers, additionalData) => {
      return (
        <Box>
          Sorry, we can't provide this loan.
          <br />
          <br />
          The unit size is too small and falls outside of our lending
          guidelines.
          <br />
          <br />
        </Box>
      );
    },
    a_type: "leaf",
    node_type: "leaf",
    q_child_nodes: [],
  },
  {
    q_id: "000000000",
    q_title: "Loan purpose",
    q_description: (answer, answers, additionalData) => {
      return "Is this a purchase or a refinance";
    },
    node_type: "node",
    a_type: "mc",
    a_input_type: "",
    a_opts: (answers) => {
      return ["Purchase", "Refinance"];
    },
    q_next_step_func: (answer, answers, questions) => {
      const loanType = answers?.filter((answer) => {
        return answer.q_title === "Loan type";
      })[0]?.a_string;

      if (loanType === "Ground up construction") {
        return "0000000002";
      }

      return answer.q_id + "0";
    },
    q_child_nodes: [0, 1],
    q_validation_func: (answerVal) => {
      // MC so any choise is valid
      return true; //isValid
    },
    q_invalid_answer_help_text: "",
    a_error: false,
  },
  {
    q_id: "0000000000010000",
    q_title: "Leasing status",
    q_description: (answer, answers, additionalData) => {
      return "Do you have a signed and current lease?";
    },
    node_type: "node",
    a_type: "mc",
    // a_input_type: "number",
    a_opts: (answers) => {
      return ["Yes", "No"];
    },
    q_next_step_func: (answer, answers, questions) => {
      return "000000000000";
    },
    q_child_nodes: [0],
    q_validation_func: (answerVal) => {
      return true;
    },
    q_invalid_answer_help_text: "Please enter a value above 0",
    a_error: false,
  },
  {
    q_id: "0000000000010001",
    q_title: "Leasing status",
    q_description: (answer, answers, additionalData) => {
      return "How many units have a signed and current lease?";
    },
    node_type: "node",
    a_type: "number",
    a_input_type: "number",
    a_opts: (answers) => {
      return [];
    },
    q_next_step_func: (answer, answers, questions) => {
      return "000000000000";
    },
    q_child_nodes: [0],
    q_validation_func: (answerVal) => {
      return true; //isValid
    },
    q_invalid_answer_help_text: "Please enter a value above 0",
    a_error: false,
  },

  {
    q_id: "0000000000000",
    q_title: "Leasing strategy", // new question, asking right before all dscr flow that used to ask for monthly rent, since logically, if its refinance, or need dscr for any reason, monthly is asked and thus leasing strategy is applicable
    q_description: (answer, answers, additionalData) => {
      return "What leasing strategy are you using or planning on using?";
    },
    node_type: "node",
    a_type: "mc",
    a_input_type: "",
    a_opts: (answers) => {
      return ["Long term rental", "Mid term rental", "Short term rental"];
    },
    q_next_step_func: (answer, answers, questions) => {
      return "000000000000000";
      // const loanPurpose = answers?.filter((answer) => {
      //   return answer.q_title === "Loan purpose";
      // })?.[0]?.a_string;

      // const loanType = answers?.filter((answer) => {
      //   return answer.q_title === "Loan type";
      // })?.[0]?.a_string;

      // if (loanType === "Fix and flip" && loanPurpose === "Refinance") {
      //   return "000000000000000"; // monthly rent
      // }
      return answer.q_id + "0";
    },
    q_child_nodes: [0],
    q_validation_func: (answerVal) => {
      if (answerVal < 0) {
        return false; //isValid
      }
      return true; //isValid
    },
    q_invalid_answer_help_text: "Please enter a value above 0",
    a_error: false,
  },
  {
    q_id: "000000000000",
    q_title: "Tenant type",
    q_description: (answer, answers, additionalData) => {
      return "What type of tenant will be inhabiting the property?";
    },
    node_type: "node",
    a_type: "mc",
    a_input_type: "",
    a_opts: (answers) => {
      const propertyType = answers.filter((answer) => {
        return answer.q_title === "Property type";
      })?.[0]?.a_string;

      let numberOfUnits = answers.filter((answer) => {
        return answer.q_title === "Number of units";
      })?.[0]?.a_string;

      if (!numberOfUnits) {
        numberOfUnits = {
          "Single family": 1,
          Condominium: 1,
          Duplex: 2,
          Triplex: 3,
          Quadplex: 4,
          "5+ unit multi-family": 5,
          "Mixed use": 2,
        }[propertyType];
      }

      const defaultOps = ["Market tenant", "Section 8"];

      if (numberOfUnits > 1) {
        defaultOps.push("Mixed use");
      }

      return defaultOps;
    },
    q_next_step_func: (answer, answers, questions) => {
      return answer.q_id + "0";
    },
    q_child_nodes: [0],
    q_validation_func: (answerVal) => {
      if (answerVal < 0) {
        return false; //isValid
      }
      return true; //isValid
    },
    q_invalid_answer_help_text: "Please enter a value above 0",
    a_error: false,
  },
  {
    q_id: "0000000002",
    q_title: "After repair value (ARV)",
    q_description: (answer, answers, additionalData) => {
      return "What is the estimated ARV once construction is completed?";
    },
    node_type: "node",
    a_type: "number",
    a_input_type: "currency",
    a_opts: (answers) => {
      return [0];
    },
    q_next_step_func: (answer, answers, questions) => {
      return answer.q_id + "0";
    },
    q_child_nodes: [0],
    q_validation_func: (answerVal) => {
      if (answerVal < 1) {
        return false; //isValid
      }
      return true; //isValid
    },
    q_invalid_answer_help_text: "Please enter a dollar amount above 0",
    a_error: false,
  },
  {
    q_id: "00000000020",
    q_title: "Professional licensing",
    q_description: (answer, answers, additionalData) => {
      return "Are you a licensed engineer or a general contractor?";
    },
    node_type: "node",
    a_type: "mc",
    a_input_type: "",
    a_opts: (answers) => {
      return [
        "Yes, same state as the property",
        "Yes, different state than the property",
        "No",
      ];
    },
    q_next_step_func: (answer, answers, questions) => {
      const loanType = answers?.filter((answer) => {
        return answer.q_title === "Loan type";
      })?.[0]?.a_string;

      if (loanType === "Ground up construction") {
        return answer.q_id + "1";
      }

      return answer.q_id + "0";
    },
    q_child_nodes: [0],
    q_validation_func: (answerVal) => {
      // MC so any choise is valid
      return true; //isValid
    },
    q_invalid_answer_help_text: "",
    a_error: false,
  },
  {
    q_id: "000000000201",
    q_title: "Ground up construction experience",
    q_description: (answer, answers, additionalData) => {
      return "How many ground up construction projects have you completed? Must be verifiable.";
    },
    node_type: "node",
    a_type: "number",
    a_input_type: "number",
    a_opts: (answers) => {
      return [];
    },
    q_next_step_func: (answer, answers, questions) => {
      const loanType = answers?.filter((answer) => {
        return answer.q_title === "Loan type";
      })[0]?.a_string;

      if (answer.a_string < 2 && loanType === "Ground up construction") {
        return answer.q_id + "1";
      }

      return answer.q_id + "0";
    },
    q_child_nodes: [0],
    q_validation_func: (answerVal) => {
      return true; //isValid
    },
    q_invalid_answer_help_text: "",
    a_error: false,
  },
  {
    q_id: "0000000002011",
    q_title: "Out of scope",
    q_description: (answer, answers, additionalData) => {
      return (
        <Box>
          We need to verify a minimum of 2 ground up experience among members of
          your borrowing entity.
          <br />
          <br />
          If you do not have this, please consider partnering with an
          experienced ground up construction developer as a member of your
          borrowing entity. Otherwise, we will not be able to fund this
          transaction.
          <br />
          <br />
        </Box>
      );
    },
    a_type: "leaf",
    node_type: "leaf",
    q_child_nodes: [],
  },
  {
    q_id: "0000000002010",
    q_title: "Permits",
    q_description: (answer, answers, additionalData) => {
      return "Do you have approved plans and permits?";
    },
    node_type: "node",
    a_type: "mc",
    a_input_type: "",
    a_opts: (answers) => {
      return ["Yes", "No"];
    },
    q_next_step_func: (answer, answers, questions) => {
      return answer.q_id + "0";
    },
    q_child_nodes: [0],
    q_validation_func: (answerVal) => {
      return true; //isValid
    },
    q_invalid_answer_help_text: "",
    a_error: false,
  },
  {
    q_id: "00000000020100",
    q_title: "Land subdivision",
    q_description: (answer, answers, additionalData) => {
      return "Do you plan to subdivide this lot?";
    },
    node_type: "node",
    a_type: "mc",
    a_input_type: "",
    a_opts: (answers) => {
      return ["Yes", "No"];
    },
    q_next_step_func: (answer, answers, questions) => {
      return answer.q_id + "0";
    },
    q_child_nodes: [0],
    q_validation_func: (answerVal) => {
      return true; //isValid
    },
    q_invalid_answer_help_text: "",
    a_error: false,
  },
  {
    q_id: "000000000200",
    q_title: "Similar project experience",
    q_description: (answer, answers, additionalData) => {
      return "How many projects of similar scope and budget have you ever completed? You must be the owner of those projects (title or LLC operating agreement).";
    },
    node_type: "node",
    a_type: "number",
    a_input_type: "number",
    a_opts: (answers) => {
      return [];
    },
    q_next_step_func: (answer, answers, questions) => {
      return answer.q_id + "0";
    },
    q_child_nodes: [0],
    q_validation_func: (answerVal, answers) => {
      const investmentExperience = answers.filter((answer) => {
        return answer.q_title === "Investment experience";
      })?.[0]?.a_string;

      if (parseInt(investmentExperience) < parseInt(answerVal)) {
        return false; //isValid
      }
      return true; //isValid
    },
    q_invalid_answer_help_text: `Can't be more than the total amount of rentals owned or flipped entered in Question 1 - Investment experience`,
    a_error: false,
  },
  {
    q_id: "00000000020000",
    q_title: "Exit strategy",
    q_description: (answer, answers, additionalData) => {
      return "What is your exit strategy for this loan?";
    },
    node_type: "node",
    a_type: "mc",
    a_input_type: "",
    a_opts: (answers) => {
      return ["Refinance", "Sale"];
    },
    q_next_step_func: (answer, answers, questions) => {
      const loanPurpose = answers?.filter((answer) => {
        return answer.q_title === "Loan purpose";
      })?.[0]?.a_string;

      const loanType = answers?.filter((answer) => {
        return answer.q_title === "Loan type";
      })?.[0]?.a_string;

      if (
        (loanType === "Fix and flip" ||
          loanType === "Ground up construction") &&
        answer.a_string === "Refinance"
      ) {
        return "000000000000";
      }

      if (loanType === "Fix and flip" && answer.a_string === "Sale") {
        return answer.q_id + "0";
      }

      if (loanType === "Ground up construction" && answer.a_string === "Sale") {
        return answer.q_id + "1";
      }
    },
    q_child_nodes: [0],
    q_validation_func: (answerVal) => {
      // all MC choices are valid
      return true; //isValid
    },
    q_invalid_answer_help_text: "",
    a_error: false,
  },
  {
    q_id: "000000000200000",
    q_title: "Fix and flip loan amount",
    q_description: (answer, answers, additionalData) => {
      // return "How much do you need for this bridge loan?";
      return "";
    },
    node_type: "node",
    a_type: "bridge_loan",
    a_opts: (answers) => {
      return [];
    },
    q_next_step_func: (answer, answers, questions) => {
      return answer.q_id + "0";
    },
    q_child_nodes: [0, 1],
    q_validation_func: (answerVal) => {
      return true; //isValid
    },
    q_invalid_answer_help_text: "",
    a_error: false,
  },
  {
    q_id: "0000000002000000",
    q_title: "Loan term",
    q_description: (answer, answers, additionalData) => {
      return "What is your expected loan term?";
    },
    node_type: "node",
    a_type: "mc",
    a_input_type: "",
    a_opts: (answers) => {
      return ["12 months", "18 months", "24 months"];
    },
    q_next_step_func: (answer, answers, questions) => {
      // show quote
      return "00000000000000000000000";
    },
    q_child_nodes: [0],
    q_validation_func: (answerVal) => {
      // all MC choices are valid
      return true; //isValid
    },
    q_invalid_answer_help_text: "",
    a_error: false,
  },
  // {
  //   q_id: "00000003",
  //   q_title: "Existing debt",
  //   q_description: (answer, answers, additionalData) => {
  //     return "Do you owe anything on this property?";
  //   },
  //   node_type: "node",
  //   a_type: "number",
  //   a_input_type: "currency",
  //   a_opts: (answers) => {
  //     return [0];
  //   },
  //   q_next_step_func: (answer, answers, questions) => {
  //     return answer.q_id + "0";
  //   },
  //   q_child_nodes: [0],
  //   q_validation_func: (answerVal) => {
  //     if (answerVal < 0) {
  //       return false; //no valid if below 0
  //     }
  //     return true; //isValid
  //   },
  //   q_invalid_answer_help_text: "Please enter a positive dollar amount",
  //   a_error: false,
  // },
  // {
  //   q_id: "000000030",
  //   q_title: "Purchase price",
  //   q_description: (answer, answers, additionalData) => {
  //     return "How much did you purchase this property for?";
  //   },
  //   node_type: "node",
  //   a_type: "number",
  //   a_input_type: "currency",
  //   a_opts: (answers) => {
  //     return [0];
  //   },
  //   q_next_step_func: (answer, answers, questions) => {
  //     return answer.q_id + "0";
  //   },
  //   q_child_nodes: [0],
  //   q_validation_func: (answerVal) => {
  //     if (answerVal < 1) {
  //       return false; //isValid
  //     }
  //     return true; //isValid
  //   },
  //   q_invalid_answer_help_text: "Please enter a dollar amount above 0",
  //   a_error: false,
  // },
  // {
  //   q_id: "0000000300",
  //   q_title: "Purchase date",
  //   q_description: (answer, answers, additionalData) => {
  //     return "When did you purchase it?";
  //   },
  //   node_type: "node",
  //   a_type: "date",
  //   a_input_type: "",
  //   a_opts: (answers) => {
  //     return [];
  //   },
  //   q_next_step_func: (answer, answers, questions) => {
  //     // this links back up with ARV question
  //     return answer.q_id + "0";
  //   },
  //   q_child_nodes: [0],
  //   q_validation_func: (answerVal) => {
  //     // mc all are valid
  //     return true; //isValid
  //   },
  //   q_invalid_answer_help_text: "",
  //   a_error: false,
  // },
  {
    q_id: "00000003000",
    q_title: "Capital improvements",
    q_description: (answer, answers, additionalData) => {
      return "How much have you invested so far into the property?";
    },
    node_type: "node",
    a_type: "number",
    a_input_type: "currency",
    a_opts: (answers) => {
      return [0];
    },
    q_next_step_func: (answer, answers, questions) => {
      return answer.q_id + "0";
    },
    q_child_nodes: [0],
    q_validation_func: (answerVal) => {
      if (answerVal < 0) {
        return false; //isValid
      }
      return true; //isValid
    },
    q_invalid_answer_help_text: "Please enter a positive dollar amount",
    a_error: false,
  },
  // {
  //   q_id: "000000030000",
  //   q_title: "Estimated AS-IS value",
  //   q_description: (answer, answers, additionalData) => {
  //     return "What is the estimated AS-IS value of the property?";
  //   },
  //   node_type: "node",
  //   a_type: "number",
  //   a_input_type: "currency",
  //   a_opts: (answers) => {
  //     return [0];
  //   },
  //   q_next_step_func: (answer, answers, questions) => {
  //     // next question is ARV in the non portfolio bridge tree
  //     return "000000020";
  //   },
  //   q_child_nodes: [0, 1],
  //   q_validation_func: (answerVal) => {
  //     if (answerVal < 1) {
  //       return false; //isValid
  //     }
  //     return true; //isValid
  //   },
  //   q_invalid_answer_help_text: "Please enter a dollar amount above 0",
  //   a_error: false,
  // },
  // {
  //   q_id: "00000004",
  //   q_title: "Land purchase price",
  //   q_description: (answer, answers, additionalData) => {
  //     return "What is the estimated purchase price for the lot of land?";
  //   },
  //   node_type: "node",
  //   a_type: "number",
  //   a_input_type: "currency",
  //   a_opts: (answers) => {
  //     return [0];
  //   },
  //   q_next_step_func: (answer, answers, questions) => {
  //     return answer.q_id + "0";
  //   },
  //   q_child_nodes: [0],
  //   q_validation_func: (answerVal) => {
  //     if (answerVal < 0) {
  //       return false; //no valid if below 0
  //     }
  //     return true; //isValid
  //   },
  //   q_invalid_answer_help_text: "Please enter a positive dollar amount",
  //   a_error: false,
  // },
  // {
  //   q_id: "000000040",
  //   q_title: "After repair value (ARV)",
  //   q_description: (answer, answers, additionalData) => {
  //     return "What is the estimated ARV once construction is completed?";
  //   },
  //   node_type: "node",
  //   a_type: "number",
  //   a_input_type: "currency",
  //   a_opts: (answers) => {
  //     return [0];
  //   },
  //   q_next_step_func: (answer, answers, questions) => {
  //     return answer.q_id + "00";
  //   },
  //   q_child_nodes: [0],
  //   q_validation_func: (answerVal) => {
  //     if (answerVal < 1) {
  //       return false; //isValid
  //     }
  //     return true; //isValid
  //   },
  //   q_invalid_answer_help_text: "Please enter a dollar amount above 0",
  //   a_error: false,
  // },
  {
    q_id: "0000000002000",
    q_title: "Construction budget",
    q_description: (answer, answers, additionalData) => {
      return "What is the expected construction budget?";
    },
    node_type: "node",
    a_type: "number",
    a_input_type: "currency",
    a_opts: (answers) => {
      return [0];
    },
    q_next_step_func: (answer, answers, questions) => {
      return answer.q_id + "0";
    },
    q_child_nodes: [0],
    q_validation_func: (answerVal) => {
      if (answerVal < 1) {
        return false; //isValid
      }
      return true; //isValid
    },
    q_invalid_answer_help_text: "Please enter a dollar amount above 0",
    a_error: false,
  },
  {
    q_id: "000000000200001",
    q_title: "Ground up construction loan amount",
    q_description: (answer, answers, additionalData) => {
      // return "How much do you need for this Ground up construction loan?";
      return "";
    },
    node_type: "node",
    a_type: "bridge_loan", // everything is the same for bridge_loan and ground_up_construction so just reusing
    a_opts: (answers) => {
      return [];
    },
    q_next_step_func: (answer, answers, questions) => {
      // ask for term (6,12,18)
      // then show quote
      return "0000000002000000";
    },
    q_child_nodes: [0, 1],
    q_validation_func: (answerVal) => {
      return true; //isValid
    },
    q_invalid_answer_help_text: "",
    a_error: false,
  },
  {
    q_id: "000000000201000",
    q_title: "Land purchase price",
    q_description: (answer, answers, additionalData) => {
      return "What is the purchace price for the lot of land?";
    },
    node_type: "node",
    a_type: "number",
    a_input_type: "currency",
    a_opts: (answers) => {
      return [0];
    },
    q_next_step_func: (answer, answers, questions) => {
      const loanPurpose = answers?.filter((answer) => {
        return answer.q_title === "Loan purpose";
      })[0]?.a_string;

      if (loanPurpose === "Refinance") {
        return answer.q_id + "1";
      }

      return "0000000002000";
    },
    q_child_nodes: [0],
    q_validation_func: (answerVal) => {
      if (answerVal < 1) {
        return false; //isValid
      }
      return true; //isValid
    },
    q_invalid_answer_help_text: "Please enter a dollar amount above 0",
    a_error: false,
  },
  {
    q_id: "0000000002010001",
    q_title: "Land purchase date",
    q_description: (answer, answers, additionalData) => {
      return "When did you purchase the land?";
    },
    node_type: "node",
    a_type: "date",
    a_input_type: "",
    a_opts: (answers) => {
      return [];
    },
    q_next_step_func: (answer, answers, questions) => {
      return answer.q_id + "0";
    },
    q_child_nodes: [0],
    q_validation_func: (answerVal) => {
      // mc all are valid
      return true; //isValid
    },
    q_invalid_answer_help_text: "",
    a_error: false,
  },
  {
    q_id: "00000000020100010",
    q_title: "Construction progress",
    q_description: (answer, answers, additionalData) => {
      return "How much have you invested into construction so far?";
    },
    node_type: "node",
    a_type: "number",
    a_input_type: "currency",
    a_opts: (answers) => {
      return [0];
    },
    q_next_step_func: (answer, answers, questions) => {
      // <><>!4
      return "0000000002000"; //
    },
    q_child_nodes: [0],
    q_validation_func: (answerVal) => {
      if (answerVal < 0) {
        return false; //isValid
      }
      return true; //isValid
    },
    q_invalid_answer_help_text: "Please enter a positive dollar amount",
    a_error: false,
  },
  // {
  //   q_id: "000000010",
  //   q_title: "Estimated AS-IS value",
  //   q_description: (answer, answers, additionalData) => {
  //     return "What is the estimated AS-IS value of the property?";
  //   },
  //   node_type: "node",
  //   a_type: "number",
  //   a_input_type: "currency",
  //   a_opts: (answers) => {
  //     return [0];
  //   },
  //   q_next_step_func: (answer, answers, questions) => {
  //     const loanType = answers?.filter((answer) => {
  //       return answer.q_title === "Loan type";
  //     })?.[0]?.a_string;

  //     // Slow flip now goes here

  //     if (loanType === "Slow flip") {
  //       if (answer.a_string < slowFlipMin) {
  //         return answer.q_id + "1";
  //       }

  //       return answer.q_id + "0";
  //     }
  //     //   this is a RENTAL REFINANCE
  //     // is this cash out or rate term refinance?
  //     const refinanceType = answers?.filter((answer) => {
  //       return answer.q_title === "Refinance type";
  //     })[0]?.a_string;

  //     if (refinanceType === "Cash out refinance") {
  //       // Cash out refinance
  //       // max: LTV 75%
  //       // min loan: 75k
  //       if (answer.a_string * 0.75 < minRentalLoan) {
  //         return answer.q_id + "1";
  //       }
  //     }
  //     if (refinanceType === "Rate term refinance") {
  //       // Rate term refinance
  //       // max: LTV 80%
  //       // min loan: 75k
  //       if (answer.a_string * 0.8 < minRentalLoan) {
  //         return answer.q_id + "1";
  //       }
  //     }

  //     const loanPurpose = answers?.filter((answer) => {
  //       return answer.q_title === "Loan purpose";
  //     })?.[0]?.a_string;

  //     const creditScore = answers?.filter((answer) => {
  //       return answer.q_title === "Credit score";
  //     })?.[0]?.a_string;

  //     switch (loanPurpose) {
  //       case "Refinance":
  //         if (creditScore < 700) {
  //           // 680 - 699	65%
  //           // ltvRanges.push({ title: "61-65", maxValue: 65 });
  //           if (answer.a_string * 0.65 < minRentalLoan) {
  //             return answer.q_id + "1";
  //           }
  //         } else if (creditScore < 720) {
  //           // 700 - 719	70%
  //           if (answer.a_string * 0.7 < minRentalLoan) {
  //             return answer.q_id + "1";
  //           }
  //         } else {
  //           // 720+	75%
  //           if (answer.a_string * 0.75 < minRentalLoan) {
  //             return answer.q_id + "1";
  //           }
  //         }

  //         break;
  //       case "Purchase":
  //         if (creditScore < 700) {
  //           // 680 - 699	70%

  //           if (answer.a_string * 0.7 < minRentalLoan) {
  //             return answer.q_id + "1";
  //           }
  //         } else if (creditScore < 720) {
  //           // 700 - 719	75%

  //           if (answer.a_string * 0.75 < minRentalLoan) {
  //             return answer.q_id + "1";
  //           }
  //         } else {
  //           // 720+	80%

  //           if (answer.a_string * 0.8 < minRentalLoan) {
  //             return answer.q_id + "1";
  //           }
  //         }
  //         break;
  //     }

  //     return answer.q_id + "0";
  //   },
  //   q_child_nodes: [0, 1],
  //   q_validation_func: (answerVal) => {
  //     if (answerVal < 1) {
  //       return false; //isValid
  //     }
  //     return true; //isValid
  //   },
  //   q_invalid_answer_help_text: "Please enter a dollar amount above 0",
  //   a_error: false,
  // },
  // {
  //   q_id: "0000000100",
  //   q_title: "Purchase price",
  //   q_description: (answer, answers, additionalData) => {
  //     return "How much did you purchase this property for?";
  //   },
  //   node_type: "node",
  //   a_type: "number",
  //   a_input_type: "currency",
  //   a_opts: (answers) => {
  //     return [0];
  //   },
  //   q_next_step_func: (answer, answers, questions) => {
  //     // this is a RENTAL REFINANCE CASH OUT
  //     // purchase price don't matter, as-is does
  //     return answer.q_id + "0";
  //   },
  //   q_child_nodes: [0],
  //   q_validation_func: (answerVal) => {
  //     // NOTE: allow 0 for DSCR refinaince since property might have been inherited
  //     // if (answerVal < 1) {
  //     //   return false; //isValid
  //     // }
  //     return true; //isValid
  //   },
  //   q_invalid_answer_help_text: "",
  //   a_error: false,
  // },
  {
    q_id: "000000000001",
    q_title: "Purchase date",
    q_description: (answer, answers, additionalData) => {
      return "When did you purchase it?";
    },
    node_type: "node",
    a_type: "date",
    a_input_type: "",
    a_opts: (answers) => {
      return [];
    },
    q_next_step_func: (answer, answers, questions) => {
      return answer.q_id + "0";
    },
    q_child_nodes: [0],
    q_validation_func: (answerVal) => {
      // mc all are valid
      return true; //isValid
    },
    q_invalid_answer_help_text: "",
    a_error: false,
  },
  {
    q_id: "0000000000010",
    q_title: "Existing debt",
    q_description: (answer, answers, additionalData) => {
      return "Do you owe anything on this property?";
    },
    node_type: "node",
    a_type: "number",
    a_input_type: "currency",
    a_opts: (answers) => {
      return [0];
    },
    q_next_step_func: (answer, answers, questions) => {
      return answer.q_id + "0";
    },
    q_child_nodes: [0],
    q_validation_func: (answerVal) => {
      if (answerVal < 0) {
        return false; //isValid
      }
      return true; //isValid
    },
    q_invalid_answer_help_text: "Please enter a positive dollar amount",
    a_error: false,
  },
  {
    q_id: "00000000000100",
    q_title: "Capital improvements",
    q_description: (answer, answers, additionalData) => {
      return "How much have you invested so far into the property?";
    },
    node_type: "node",
    a_type: "number",
    a_input_type: "currency",
    a_opts: (answers) => {
      return [0];
    },
    q_next_step_func: (answer, answers, questions) => {
      const loanPurpose = answers?.filter((answer) => {
        return answer.q_title === "Loan purpose";
      })?.[0]?.a_string;

      const loanType = answers?.filter((answer) => {
        return answer.q_title === "Loan type";
      })?.[0]?.a_string;

      if (loanType === "Fix and flip" && loanPurpose === "Refinance") {
        return "0000000002"; // ARV
      }

      return answer.q_id + "0";
    },
    q_child_nodes: [0],
    q_validation_func: (answerVal) => {
      if (answerVal < 0) {
        return false; //isValid
      }
      return true; //isValid
    },
    q_invalid_answer_help_text: "Please enter a positive dollar amount",
    a_error: false,
  },
  {
    q_id: "000000000001000",
    q_title: "Refinance type",
    q_description: (answer, answers, additionalData) => {
      return "What type of refinance do you want to complete?";
    },
    node_type: "node",
    a_type: "mc",
    a_input_type: "",
    a_opts: (answers) => {
      return ["Cash out refinance", "Rate term refinance"];
    },
    q_next_step_func: (answer, answers, questions) => {
      // return "000000000000000"; // monthly rent
      const propertyType = answers.filter((answer) => {
        return answer.q_title === "Property type";
      })?.[0]?.a_string;

      let numberOfUnits = answers.filter((answer) => {
        return answer.q_title === "Number of units";
      })?.[0]?.a_string;

      if (!numberOfUnits) {
        numberOfUnits = {
          "Single family": 1,
          Duplex: 2,
          Triplex: 3,
          Quadplex: 4,
          Condominium: 1,
        }[propertyType];
      }

      if (numberOfUnits === 1) {
        return answer.q_id + "0";
      } else {
        return answer.q_id + "1";
      }
    },
    q_child_nodes: [0, 1],
    q_validation_func: (answerVal) => {
      // mc all are valid
      return true; //isValid
    },
    q_invalid_answer_help_text: "",
    a_error: false,
  },
  {
    q_id: "0000000101",
    q_title: "Out of scope",
    q_description: (answer, answers, additionalData) => {
      const estimatedASISPriceQuestionQID = "000000010";
      const estimatedASISPrice = answers.filter((answer) => {
        return answer.q_id === estimatedASISPriceQuestionQID;
      })[0].a_string;

      // is this cash out or rate term refinance?
      const refinanceType = answers.filter((answer) => {
        return answer.q_title === "Refinance type";
      })[0]?.a_string;

      if (refinanceType === "Cash out refinance") {
        // Cash out refinance

        // max: LTV 75%
        // min loan: 75k
        return `Sorry we can't underwrite this rental cash out refinance loan. The loan amount based on maximum Loan To Value (LTV) of 75% can't be below $${formatMoney(
          minRentalLoan,
          0
        )}. Based on the price you have entered of $${formatMoney(
          estimatedASISPrice,
          0
        )} and maximum LTV of 75%, loan amount would be $${formatMoney(
          estimatedASISPrice * 0.75,
          0
        )} which is below our minimum of $75,000`;
      }
      if (refinanceType === "Rate term refinance") {
        // Rate term refinance
        // max: LTV 80%
        // min loan: 75k
        return `Sorry we can't underwrite this rental rate term refinance loan. The loan amount based on maximum Loan To Value (LTV) of 80% can't be below $${formatMoney(
          minRentalLoan,
          0
        )}. Based on the price you have entered of $${formatMoney(
          estimatedASISPrice,
          0
        )} and maximum LTV of 80%, loan amount would be $${formatMoney(
          estimatedASISPrice * 0.8,
          0
        )} which is below our minimum of $75,000`;
      }
    },
    a_type: "leaf",
    node_type: "leaf",
    q_child_nodes: [],
  },
  {
    q_id: "0000000000",
    q_title: "Estimated AS-IS value",
    q_description: (answer, answers, additionalData) => {
      return "What is your best guess at the AS-IS value irrespective of the purchase price?";
    },
    node_type: "node",
    a_type: "number",
    a_input_type: "currency",
    a_opts: (answers) => {
      return [];
    },
    q_next_step_func: (answer, answers, questions) => {
      const loanType = answers?.filter((answer) => {
        return answer.q_title === "Loan type";
      })?.[0]?.a_string;

      if (loanType === "Fix and flip") {
        return answer.q_id + "0"; // estimated as-is doenst matter for FnF
      }
      // Slow flip now goes here
      if (loanType === "Slow flip") {
        if (answer.a_string < slowFlipMin) {
          return answer.q_id + "1";
        }

        return answer.q_id + "0";
      }

      const loanPurpose = answers?.filter((answer) => {
        return answer.q_title === "Loan purpose";
      })?.[0]?.a_string;

      const creditScore = answers?.filter((answer) => {
        return answer.q_title === "Credit score";
      })?.[0]?.a_string;

      switch (loanPurpose) {
        case "Refinance":
          if (creditScore < 700) {
            // 680 - 699	65%
            // ltvRanges.push({ title: "61-65", maxValue: 65 });
            if (answer.a_string * 0.65 < minRentalLoan) {
              return answer.q_id + "1";
            }
          } else if (creditScore < 720) {
            // 700 - 719	70%
            // ltvRanges.push({ title: "61-65", maxValue: 65 });
            // ltvRanges.push({ title: "66-70", maxValue: 70 });

            if (answer.a_string * 0.7 < minRentalLoan) {
              return answer.q_id + "1";
            }
          } else {
            // 720+	75%
            // ltvRanges.push({ title: "61-65", maxValue: 65 });
            // ltvRanges.push({ title: "66-70", maxValue: 70 });
            // ltvRanges.push({ title: "71-75", maxValue: 75 });
            if (answer.a_string * 0.75 < minRentalLoan) {
              return answer.q_id + "1";
            }
          }

          break;
        case "Purchase":
          if (creditScore < 700) {
            // 680 - 699	70%
            // ltvRanges.push({ title: "61-65", maxValue: 65 });
            // ltvRanges.push({ title: "66-70", maxValue: 70 });
            if (answer.a_string * 0.7 < minRentalLoan) {
              return answer.q_id + "1";
            }
          } else if (creditScore < 720) {
            // 700 - 719	75%
            // ltvRanges.push({ title: "61-65", maxValue: 65 });
            // ltvRanges.push({ title: "66-70", maxValue: 70 });
            // ltvRanges.push({ title: "71-75", maxValue: 75 });
            if (answer.a_string * 0.75 < minRentalLoan) {
              return answer.q_id + "1";
            }
          } else {
            // 720+	80%
            // ltvRanges.push({ title: "61-65", maxValue: 65 });
            // ltvRanges.push({ title: "66-70", maxValue: 70 });
            // ltvRanges.push({ title: "71-75", maxValue: 75 });
            // ltvRanges.push({ title: "76-80", maxValue: 80 });
            if (answer.a_string * 0.8 < minRentalLoan) {
              return answer.q_id + "1";
            }
          }
          break;
      }

      return answer.q_id + "0";
    },
    q_child_nodes: [0],
    q_validation_func: (answerVal) => {
      if (answerVal < 0) {
        return false; //isValid
      }
      return true; //isValid
    },
    q_invalid_answer_help_text: "Please enter a value above 0",
    a_error: false,
  },
  {
    q_id: "00000000001",
    q_title: "Out of scope",
    q_description: (answer, answers, additionalData) => {
      const estimatedASISPrice = answers.filter((answer) => {
        return answer.q_title === "Estimated AS-IS value";
      })[0].a_string;

      const loanPurpose = answers?.filter((answer) => {
        return answer.q_title === "Loan purpose";
      })?.[0]?.a_string;

      const loanType = answers?.filter((answer) => {
        return answer.q_title === "Loan type";
      })?.[0]?.a_string;

      const creditScore = answers?.filter((answer) => {
        return answer.q_title === "Credit score";
      })?.[0]?.a_string;

      let maxLTV;
      switch (loanPurpose) {
        case "Refinance":
          if (creditScore < 700) {
            // 680 - 699	65%
            // ltvRanges.push({ title: "61-65", maxValue: 65 });
            maxLTV = 65;
          } else if (creditScore < 720) {
            // 700 - 719	70%
            // ltvRanges.push({ title: "61-65", maxValue: 65 });
            // ltvRanges.push({ title: "66-70", maxValue: 70 });
            maxLTV = 70;
          } else {
            // 720+	75%
            // ltvRanges.push({ title: "61-65", maxValue: 65 });
            // ltvRanges.push({ title: "66-70", maxValue: 70 });
            // ltvRanges.push({ title: "71-75", maxValue: 75 });
            maxLTV = 75;
          }

          break;
        case "Purchase":
          if (creditScore < 700) {
            // 680 - 699	70%
            // ltvRanges.push({ title: "61-65", maxValue: 65 });
            // ltvRanges.push({ title: "66-70", maxValue: 70 });
            maxLTV = 70;
          } else if (creditScore < 720) {
            // 700 - 719	75%
            // ltvRanges.push({ title: "61-65", maxValue: 65 });
            // ltvRanges.push({ title: "66-70", maxValue: 70 });
            // ltvRanges.push({ title: "71-75", maxValue: 75 });
            maxLTV = 75;
          } else {
            // 720+	80%
            // ltvRanges.push({ title: "61-65", maxValue: 65 });
            // ltvRanges.push({ title: "66-70", maxValue: 70 });
            // ltvRanges.push({ title: "71-75", maxValue: 75 });
            // ltvRanges.push({ title: "76-80", maxValue: 80 });
            maxLTV = 80;
          }
          break;
      }

      return `Sorry we can't underwrite this ${loanType} ${loanPurpose} loan. The loan amount based on maximum Loan To Value (LTV) of ${maxLTV} (the maximum LTV we can do on this loan) can't be below $${formatMoney(
        minRentalLoan,
        0
      )}. Based on the price you have entered of $${formatMoney(
        estimatedASISPrice,
        0
      )} and maximum LTV of ${maxLTV} (that we can provide), loan amount would be $${formatMoney(
        (estimatedASISPrice * maxLTV) / 100,
        0
      )} which is below our minimum of $75,000`;
    },
    a_type: "leaf",
    node_type: "leaf",
    q_child_nodes: [],
  },
  {
    q_id: "00000000000",
    q_title: "Purchase price",
    q_description: (answer, answers, additionalData) => {
      return "What is the estimated purchase price?";
    },
    node_type: "node",
    a_type: "number",
    a_input_type: "currency",
    a_opts: (answers) => {
      return [0];
    },
    q_next_step_func: (answer, answers, questions) => {
      const portfolio = answers?.filter((answer) => {
        return answer.q_title === "Portfolio loan";
      })[0]?.a_string;

      const loanType = answers?.filter((answer) => {
        return answer.q_title === "Loan type";
      })[0]?.a_string;

      const loanPurpose = answers?.filter((answer) => {
        return answer.q_title === "Loan purpose";
      })[0]?.a_string;

      if (portfolio === "Yes") {
        if (loanPurpose === "Refinance") {
          return answer.q_id + "10";
        }

        if (loanPurpose === "Purchase") {
          return answer.q_id + "0";
        }
      }

      if (
        loanType === "DSCR" &&
        loanPurpose === "Purchase" &&
        answer.a_string * maxRentalLTV < minRentalLoan
      ) {
        //   this loan value is too low
        return answer.q_id + "3";
      }

      if (
        (loanType === "DSCR" || loanType === "Slow flip") &&
        loanPurpose === "Purchase"
      ) {
        return "000000000000"; // TODO fix
      }

      if (
        (loanType === "DSCR" ||
          loanType === "Slow flip" ||
          loanType === "Fix and flip") &&
        loanPurpose === "Refinance"
      ) {
        return answer.q_id + "1";
      }

      if (loanType === "Fix and flip" && loanPurpose === "Purchase") {
        return "0000000002";
      }
    },
    q_child_nodes: [0, 1],
    q_validation_func: (answerVal) => {
      if (answerVal < 1) {
        return false; //isValid
      }
      return true; //isValid
    },
    q_invalid_answer_help_text: "Please enter a dollar amount above 0",
    a_error: false,
  },
  {
    q_id: "000000000003",
    q_title: "Out of scope",
    q_description: (answer, answers, additionalData) => {
      const purchasePrice = answers.filter((answer) => {
        return answer.q_title === "Purchase price";
      })[0].a_string;

      return `Sorry we can't underwrite this loan. DSCR loan amount based on maximum Loan To Value (LTV) of 80% can't be below $${formatMoney(
        minRentalLoan,
        0
      )}. Based on the price you have entered of $${formatMoney(
        purchasePrice,
        0
      )} and maximum LTV of 80%, loan amount would be $${formatMoney(
        purchasePrice * maxRentalLTV,
        0
      )} which is below our minimum of $75,000`;
    },
    a_type: "leaf",
    node_type: "leaf",
    q_child_nodes: [],
  },
  {
    q_id: "000000000000000",
    q_title: "Monthly rent",
    q_description: (answer, answers, additionalData) => {
      const loanType = answers?.filter((answer) => {
        return answer.q_title === "Loan type";
      })[0]?.a_string;
      const exitStrategy = answers?.filter((answer) => {
        return answer.q_title === "Exit strategy";
      })[0]?.a_string;

      let desc;
      switch (loanType) {
        case "DSCR":
          desc =
            "What is the lower of actual rent or market rent? If any unit is not leased, estimate the market rent and provide the sum for the whole property or properties is this is a portfolio loan.";
          break;
        case "Fix and flip":
          if (exitStrategy === "Refinance") {
            desc =
              "How much will the property rent for once the rehab is completed?";
          }
          break;
        case "Ground up construction":
          if (exitStrategy === "Refinance") {
            desc =
              "How much will the property rent for once the construction is completed?";
          }
          break;
      }
      return desc;
    },
    node_type: "node",
    a_type: "number",
    a_input_type: "currency",
    a_opts: (answers) => {
      return [];
    },
    q_next_step_func: (answer, answers, questions) => {
      return answer.q_id + "0";
    },
    q_child_nodes: [0],
    q_validation_func: (answerVal) => {
      if (answerVal < 0) {
        return false; //isValid
      }
      return true; //isValid
    },
    q_invalid_answer_help_text: "Please enter a value above 0",
    a_error: false,
  },
  {
    q_id: "0000000000000000",
    q_title: "Annual taxes",
    q_description: (answer, answers, additionalData) => {
      return "What is actual or estimated annual tax?";
    },
    node_type: "node",
    a_type: "number",
    a_input_type: "currency",
    a_opts: (answers) => {
      return [];
    },
    q_next_step_func: (answer, answers, questions) => {
      return answer.q_id + "0";
    },
    q_child_nodes: [0],
    q_validation_func: (answerVal) => {
      if (answerVal < 0) {
        return false; //isValid
      }
      return true; //isValid
    },
    q_invalid_answer_help_text: "Please enter a value above 0",
    a_error: false,
  },
  {
    q_id: "00000000000000000",
    q_title: "Annual insurance",
    q_description: (answer, answers, additionalData) => {
      return "What is actual or estimated annual insurance?";
    },
    node_type: "node",
    a_type: "number",
    a_input_type: "currency",
    a_opts: (answers) => {
      return [];
    },
    q_next_step_func: (answer, answers, questions) => {
      return answer.q_id + "0";
    },
    q_child_nodes: [0],
    q_validation_func: (answerVal) => {
      if (answerVal < 0) {
        return false; //isValid
      }
      return true; //isValid
    },
    q_invalid_answer_help_text: "Please enter a value above 0",
    a_error: false,
  },
  {
    q_id: "000000000000000000",
    q_title: "Annual HOA",
    q_description: (answer, answers, additionalData) => {
      return "What is actual or estimated annual HOA?";
    },
    node_type: "node",
    a_type: "number",
    a_input_type: "currency",
    a_opts: (answers) => {
      return [];
    },
    q_next_step_func: (answer, answers, questions) => {
      const loanType = answers?.filter((answer) => {
        return answer.q_title === "Loan type";
      })[0]?.a_string;
      const exitStrategy = answers?.filter((answer) => {
        return answer.q_title === "Exit strategy";
      })[0]?.a_string;

      if (loanType === "Fix and flip" && exitStrategy === "Refinance") {
        // if this is Bridge and exit strategy is refinance
        // then need to take them back to that flow
        // next question is "Fix and flip loan amount"
        return "000000000200000";
      }

      if (
        loanType === "Ground up construction" &&
        exitStrategy === "Refinance"
      ) {
        // if this is Bridge and exit strategy is refinance
        // then need to take them back to that flow
        // next question is "Fix and flip loan amount"
        return "000000000200001";
      }

      if (loanType === "Slow flip") {
        return "0000000000000000001";
      }

      // additional 0000 here
      // because deleted bankruptcy and foreclosure questions
      // as well as annual utility expenses and annual property expense
      // to stream line this stepper
      // these questions are now asked in the personalBackgroundQuestions
      return answer.q_id + "00000";
    },
    q_child_nodes: [0],
    q_validation_func: (answerVal) => {
      if (answerVal < 0) {
        return false; //isValid
      }
      return true; //isValid
    },
    q_invalid_answer_help_text: "Please enter a value above 0",
    a_error: false,
  },

  {
    q_id: "0000000000000000001",
    q_title: "Slow flip group",
    q_description: (answer, answers, additionalData) => {
      return "Are you a member of Scott Jelinek's Slow Flip mastermind group?";
    },
    node_type: "node",
    a_type: "mc",
    a_input_type: "",
    a_opts: (answers) => {
      return ["Yes", "No"];
    },
    q_next_step_func: (answer, answers, questions) => {
      // show quote after this question
      return "00000000000000000000000";
    },
    q_child_nodes: [0],
    q_validation_func: (answerVal) => {
      if (answerVal < 0) {
        return false; //isValid
      }
      return true; //isValid
    },
    q_invalid_answer_help_text: "Please enter a value above 0",
    a_error: false,
  },
  {
    q_id: "00000000000000000000000",
    q_title: "Citizenship status",
    q_description: (answer, answers, additionalData) => {
      return "";
    },
    node_type: "node",
    a_type: "mc",
    a_input_type: "",
    a_opts: (answers) => {
      return ["US citizen", "Green card holder", "Foreign national"];
    },
    q_next_step_func: (answer, answers, questions) => {
      return answer.q_id + "0";
    },
    q_child_nodes: [0, 1],
    q_validation_func: (answerVal) => {
      return true; //isValid
    },
    q_invalid_answer_help_text: "",
    a_error: false,
  },
  {
    q_id: "000000000000000000000000",
    q_title: "Calculating Quote",
    q_description: (answer, answers, additionalData) => {
      return "";
    },
    node_type: "node",
    a_type: "final_quote",
    a_input_type: "",
    a_opts: (answers) => {
      return [];
    },
    q_next_step_func: (answer, answers, questions) => {
      // NOTE: a_type:"final_quote" is special
      // in loanstepper > processQuote takes over some of the functionality
      // that could be here but its not standardized for usual next_step action
      return answer.q_id + "0";
    },
    q_child_nodes: [0, 1],
    q_validation_func: (answerVal) => {
      // final_quote
      return true; //isValid
    },
    q_invalid_answer_help_text: "",
    a_error: false,
  },
  {
    q_id: "0000000000000000000000000",
    q_title: "Account",
    q_description: (answer, answers, additionalData) => {
      return "";
    },
    node_type: "authenticator",
    a_type: "authenticator",
    a_input_type: "",
    a_opts: (answers) => {
      return [];
    },
    q_next_step_func: (answer, answers, questions) => {
      // final_quote is special
      // in loanstepper > processQuote takes over some of the functionality
      // that could be here but its not standardized for usual next_step action
      return answer.q_id + "0";
    },
    q_child_nodes: [0, 1],
    q_validation_func: (answerVal) => {
      // final_quote
      return true; //isValid
    },
    q_invalid_answer_help_text: "",
    a_error: false,
  },
  {
    q_id: "00000000000000000000000000",
    q_title: "First name",
    q_description: (answer, answers, additionalData) => {
      return "What is your first name?";
    },
    node_type: "node",
    a_type: "text",
    a_input_type: "text",
    a_opts: (answers) => {
      return [];
    },
    q_next_step_func: (answer, answers, questions) => {
      return answer.q_id + "0";
    },
    q_child_nodes: [0, 1],
    q_validation_func: (answerVal) => {
      // text
      return true; //isValid
    },
    q_invalid_answer_help_text: "",
    a_error: false,
  },
  {
    q_id: "000000000000000000000000000",
    q_title: "Last name",
    q_description: (answer, answers, additionalData) => {
      return "What is your last name?";
    },
    node_type: "node",
    a_type: "text",
    a_input_type: "text",
    a_opts: (answers) => {
      return [];
    },
    q_next_step_func: (answer, answers, questions) => {
      return answer.q_id + "00";
    },
    q_child_nodes: [0, 1],
    q_validation_func: (answerVal) => {
      // text
      return true; //isValid
    },
    q_invalid_answer_help_text: "",
    a_error: false,
  },
  {
    q_id: "00000000000000000000000000000",
    q_title: "Phone",
    q_description: (answer, answers, additionalData) => {
      return "What is your phone number?";
    },
    node_type: "node",
    a_type: "phone_number",
    a_input_type: "",
    a_opts: (answers) => {
      return [];
    },
    q_next_step_func: (answer, answers, questions) => {
      // phone is always second to last
      // so next is my_loan

      return "00000000000000000000000000000000000000000000000";
    },
    q_child_nodes: [0, 1],
    q_validation_func: (answerVal) => {
      // phone validation
      if (!answerVal || answerVal.length < 10) {
        return false;
      }
      return true; //isValid
    },
    q_invalid_answer_help_text: "Please enter a valid phone number",
    a_error: false,
  },
  {
    q_id: "00000000000000000000000000000000000000000000000",
    q_title: "My loan",
    q_description: (answer, answers, additionalData) => {
      return "";
    },
    node_type: "leaf",
    a_type: "my_loan",
    a_input_type: "",
    a_opts: (answers) => {
      return [];
    },
    q_next_step_func: (answer, answers, questions) => {
      return answer.q_id + "0";
    },
    q_child_nodes: [],
    q_validation_func: (answerVal) => {
      return true; //isValid
    },
    q_invalid_answer_help_text: "",
    a_error: false,
  },
];

export default questions;
